// Pure helpers for the Unified Code file uploader: `accept` parsing, type/size validation and
// human-readable sizes. No Angular dependency so they can be unit-tested directly.

export type FileValidationError = 'unsupportedType' | 'tooLarge';

/** Upload status shown on a file row. `pending` = selected but not sent to the server yet. */
export type FileUploadStatus = 'pending' | 'uploading' | 'completed' | 'failed';

export interface FileLike {
  name: string;
  size: number;
  type?: string;
}

/** Splits an `accept` attribute into lower-case tokens; an empty list means "any type". */
export function parseAccept(accept: string | null | undefined): string[] {
  return (accept ?? '')
    .split(',')
    .map((token) => token.trim().toLowerCase())
    .filter((token) => token.length > 0 && token !== '*' && token !== '*/*');
}

/** Checks a file against `accept` tokens (".pdf", "image/*", "application/pdf"). */
export function isAcceptedType(file: FileLike, accept: string | null | undefined): boolean {
  const tokens = parseAccept(accept);
  if (tokens.length === 0) return true;

  const name = (file.name ?? '').toLowerCase();
  const mime = (file.type ?? '').toLowerCase();
  return tokens.some((token) => {
    if (token.startsWith('.')) return name.endsWith(token);
    if (token.endsWith('/*')) return mime.startsWith(token.slice(0, -1));
    return mime === token;
  });
}

/** Returns the first validation error for the file, or null when it can be accepted. */
export function validateFile(
  file: FileLike,
  accept: string | null | undefined,
  maxSizeMb: number | null | undefined
): FileValidationError | null {
  if (!isAcceptedType(file, accept)) return 'unsupportedType';
  if (maxSizeMb && maxSizeMb > 0 && file.size > maxSizeMb * 1024 * 1024) return 'tooLarge';
  return null;
}

/** "PDF, DOC, DOCX" from ".pdf,.doc,.docx"; mime tokens are kept as written. Empty for any type. */
export function acceptLabel(accept: string | null | undefined): string {
  return parseAccept(accept)
    .map((token) => (token.startsWith('.') ? token.slice(1).toUpperCase() : token))
    .join(', ');
}

/** Binary units (1 KB = 1024 B), two decimals above bytes. */
export function formatFileSize(bytes: number | null | undefined): string {
  if (bytes == null || bytes < 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value.toFixed(2)} ${units[unit]}`;
}
